import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import moment from "moment";
import styled from "styled-components";

import HtmlHead from "components/atoms/HtmlHead";
import Icon from "components/atoms/Icon";
import { TagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Text from "components/atoms/text/Text";
import { BodyWithBreadcrumb, Breadcrumb, BreadcrumbTitle } from "components/molecules/Breadcrumb";
import LoginUserLayout from "components/templates/LoginUserLayout";
import SpaceMemberProfileModal from "components/page/modal/SpaceMemberProfileModal";

import { useSpaceActivityHistory, useSpaceDashboard, useUser } from "hooks/queries/useUser";

const COUNT_CARDS = [
    { key: "reference", label: "참고문헌", icon: "sideBarReference", path: "reference" },
    { key: "note", label: "연구노트", icon: "sideBarNote", path: "note" },
    { key: "manuscript", label: "논문원고", icon: "sideBarManuscript", path: "manuscript" },
];

const RESOURCE_LABEL = {
    reference: "참고문헌",
    note: "연구노트",
    manuscript: "논문원고",
};

const ACTIVITY_LEVELS = [
    { level: 0, label: "0건" },
    { level: 1, label: "1건" },
    { level: 2, label: "2~4건" },
    { level: 3, label: "5~9건" },
    { level: 4, label: "10건 이상" },
];

const DashboardBody = styled(BodyWithBreadcrumb)`
    gap: 32px;
    padding-bottom: 80px;
`;

const PageHeading = styled.div`
    display: flex;
    align-items: baseline;
    gap: 12px;
`;

const CardGrid = styled.section`
    display: grid;
    grid-template-columns: repeat(3, minmax(180px, 1fr));
    gap: 16px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const CountCard = styled.button`
    display: flex;
    align-items: center;
    gap: 16px;
    min-height: 112px;
    padding: 24px;
    border: 1px solid var(--color-Line);
    border-radius: 8px;
    background: var(--color-White);
    text-align: left;
    cursor: pointer;

    &:hover {
        border-color: var(--color-Key);
        background: var(--color-ButtonHover4);
    }
`;

const CardIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    background: var(--color-Base1);
`;

const CardContent = styled.span`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const CardCount = styled.span`
    color: var(--color-Black);
    font-size: 2.4rem;
    font-weight: 600;
`;

const Section = styled.section`
    min-width: 0;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
`;

const SectionTitle = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
`;

const SectionTitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 20px;
`;

const SelectedDateTag = styled(TagItem).attrs({ as: "button", type: "button" })`
    flex-shrink: 0;
    gap: 5px;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid var(--color-Key);
        outline-offset: 2px;
    }
`;

const ActivityPanel = styled.div`
    padding: 18px 24px;
    border: 1px solid var(--color-Line);
    border-radius: 8px;
    background: var(--color-White);
`;

const StripScroll = styled.div`
    width: 100%;
    padding-bottom: 4px;
`;

const Strip = styled.div`
    display: grid;
    grid-template-columns: repeat(30, minmax(0, 1fr));
    gap: clamp(2px, 0.4vw, 6px);
    width: 100%;
`;

const ActivityCell = styled.button`
    display: block;
    width: 100%;
    height: 22px;
    box-sizing: border-box;
    padding: 0;
    border: 1px solid
        ${({ $level, $selected }) =>
            $selected ? "var(--color-SubBlack)" : $level === 0 ? "var(--color-Line)" : "transparent"};
    border-radius: 4px;
    background: ${({ $level }) => ["var(--color-Base1)", "#d7f3e8", "#9fdfc8", "#4fbd91", "var(--color-Key)"][$level]};
    box-shadow: ${({ $selected }) => ($selected ? "inset 0 0 0 1px var(--color-SubBlack)" : "none")};
    cursor: pointer;

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid var(--color-Key);
        outline-offset: 2px;
    }
`;

const StripLabels = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 8px;
    color: var(--color-Grey1);
    font-size: 1.2rem;
`;

const ActivityMetrics = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--color-Line);
`;

const ActivityMetric = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: var(--color-Grey1);
    font-size: 1.2rem;

    strong {
        color: var(--color-SubBlack);
        font-size: 1.3rem;
        font-weight: 500;
    }
`;

const Legend = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--color-Grey1);
    font-size: 1.2rem;
`;

const ActivitySummary = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`;

const SummaryDivider = styled.span`
    width: 1px;
    height: 12px;
    background: var(--color-Line);
`;

const LegendCell = styled(ActivityCell)`
    width: 14px;
    height: 14px;
    cursor: default;
`;

const EventList = styled.div`
    border-top: 1px solid var(--color-Line);
`;

const DateHeading = styled(Text).attrs({ variant: "body-md-500" })`
    margin: 24px 0 10px;
`;

const EventRow = styled.div`
    display: grid;
    grid-template-columns: 60px 28px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
    min-height: 68px;
    padding: 14px 8px;
    border-bottom: 1px solid var(--color-Line);
`;

const EventTime = styled.time`
    padding-top: 5px;
    color: var(--color-Grey1);
    font-size: 1.2rem;
`;

const EventIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid var(--color-Line);
    border-radius: 50%;
`;

const EventBody = styled.div`
    min-width: 0;
    color: var(--color-SubBlack);
    font-size: 1.4rem;
    line-height: 1.7;
`;

const TargetButton = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--color-Key);
    font: inherit;
    text-decoration: underline;
    cursor: pointer;
`;

const ActorButton = styled(TargetButton)`
    text-decoration: underline;
`;

const Preview = styled.div`
    display: -webkit-box;
    margin-top: 4px;
    overflow: hidden;
    color: var(--color-Grey1);
    font-size: 1.2rem;
    line-height: 1.5;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const StateBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
    border: 1px solid var(--color-Line);
    border-radius: 8px;
    color: var(--color-Grey1);
    font-size: 1.4rem;
`;

const TextButton = styled.button`
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--color-Key);
    font-size: 1.3rem;
    cursor: pointer;
`;

function findEventLink(event, type) {
    return event.links?.find((link) => link.type === type) || null;
}

function buildEventUrl(event) {
    if (!event.primary_target.url) return null;
    const revision = findEventLink(event, "revision");
    const thread = findEventLink(event, "review_thread");
    const message = findEventLink(event, "review_message");
    if (!revision && !thread) return event.primary_target.url;

    const [pathname, currentSearch = ""] = event.primary_target.url.split("?");
    const params = new URLSearchParams(currentSearch);
    if (revision) {
        params.set("panel", "history");
        params.set("revision", String(revision.target_id));
        return `${pathname}?${params.toString()}`;
    }
    if (event.resource_type === "reference") {
        const selection = findEventLink(event, "review_selection");
        params.set("view", "pdf");
        if (selection?.page) params.set("page", String(selection.page));
        if (selection?.target_id) params.set("selection", String(selection.target_id));
    } else {
        params.set("panel", "review");
    }
    params.set("thread", String(thread.target_id));
    if (message?.target_id) params.set("message", String(message.target_id));
    return `${pathname}?${params.toString()}`;
}

function activityLevel(total) {
    if (total === 0) return 0;
    if (total === 1) return 1;
    if (total < 5) return 2;
    if (total < 10) return 3;
    return 4;
}

function ActivitySentence({ event, navigate, onMemberClick }) {
    const targetUrl = buildEventUrl(event);
    const target = targetUrl ? (
        <TargetButton type="button" onClick={() => navigate(targetUrl)}>
            {event.primary_target.label}
        </TargetButton>
    ) : (
        <strong>{event.primary_target.label}</strong>
    );
    const actor = (
        <>
            <ActorButton type="button" onClick={() => onMemberClick(event.actor)}>
                {event.actor.name}
            </ActorButton>{" "}
            님이
        </>
    );

    switch (event.event_type) {
        case "reference.created":
            return (
                <>
                    {actor} 참고문헌 {target}을 업로드했습니다.
                </>
            );
        case "reference.review_message_created":
            return (
                <>
                    {actor} 참고문헌 {target}에 리뷰 메시지를 추가했습니다.
                </>
            );
        case "note.created":
            return (
                <>
                    {actor} 연구노트 {target}를 추가했습니다.
                </>
            );
        case "note.revision_created":
            return (
                <>
                    {actor} 연구노트 {target}의 리비전 #{event.metadata.revision_seq}을 생성했습니다.
                </>
            );
        case "note.review_message_created":
            return (
                <>
                    {actor} 연구노트 {target}에 리뷰 메시지를 추가했습니다.
                </>
            );
        case "manuscript.created":
            return (
                <>
                    {actor} 논문원고 {target}를 추가했습니다.
                </>
            );
        case "manuscript.revision_created":
            return (
                <>
                    {actor} 논문원고 {target}의 리비전 #{event.metadata.revision_seq}을 생성했습니다.
                </>
            );
        case "manuscript.review_message_created":
            return (
                <>
                    {actor} 논문원고 {target}에 리뷰 메시지를 추가했습니다.
                </>
            );
        default:
            return null;
    }
}

function DashboardPage() {
    const { space_uuid } = useParams();
    const navigate = useNavigate();
    const user = useUser().data;
    const [selectedDate, setSelectedDate] = useState("");
    const [profileMember, setProfileMember] = useState(null);
    const dashboardQuery = useSpaceDashboard(space_uuid);
    const activityRange = dashboardQuery.data?.activity;
    const activityParams = useMemo(
        () => ({
            from: selectedDate || activityRange?.from,
            to: selectedDate || activityRange?.to,
            limit: 10,
        }),
        [activityRange?.from, activityRange?.to, selectedDate],
    );
    const historyQuery = useSpaceActivityHistory(space_uuid, activityParams, {
        enabled: Boolean(space_uuid && activityRange?.from),
    });
    const events = useMemo(
        () => historyQuery.data?.pages?.flatMap((page) => page.results) || [],
        [historyQuery.data?.pages],
    );
    const groupedEvents = useMemo(
        () =>
            events.reduce((groups, event) => {
                const date = moment(event.occurred_at).format("YYYY-MM-DD");
                if (!groups[date]) groups[date] = [];
                groups[date].push(event);
                return groups;
            }, {}),
        [events],
    );
    const activityStats = useMemo(() => {
        const daily = activityRange?.daily || [];
        const activeDays = daily.filter((item) => item.total > 0).length;
        let consecutiveDays = 0;
        for (let index = daily.length - 1; index >= 0 && daily[index].total > 0; index -= 1) {
            consecutiveDays += 1;
        }
        return { activeDays, consecutiveDays };
    }, [activityRange?.daily]);
    const spaceName = user?.current_space?.name || "스페이스";

    return (
        <LoginUserLayout>
            <HtmlHead title={`${spaceName} 대시보드`} />
            <Breadcrumb>
                <PageHeading>
                    <BreadcrumbTitle>대시보드</BreadcrumbTitle>
                    <Text variant="body-sm-400" color="var(--color-Grey1)">
                        {spaceName}
                    </Text>
                </PageHeading>
            </Breadcrumb>
            <DashboardBody>
                {dashboardQuery.isLoading ? (
                    <StateBox>대시보드를 불러오는 중입니다.</StateBox>
                ) : dashboardQuery.isError ? (
                    <StateBox>대시보드를 불러오지 못했습니다.</StateBox>
                ) : (
                    <>
                        <CardGrid>
                            {COUNT_CARDS.map((card) => (
                                <CountCard
                                    key={card.key}
                                    type="button"
                                    onClick={() => navigate(`/${space_uuid}/${card.path}`)}
                                >
                                    <CardIcon>
                                        <Icon name={card.icon} size="20px" color="var(--color-Key)" />
                                    </CardIcon>
                                    <CardContent>
                                        <Text variant="body-md-500">{card.label}</Text>
                                        <CardCount>{dashboardQuery.data?.counts?.[card.key] || 0}건</CardCount>
                                    </CardContent>
                                </CountCard>
                            ))}
                        </CardGrid>

                        <Section>
                            <SectionHeader>
                                <SectionTitle>최근 30일</SectionTitle>
                                <ActivitySummary>
                                    <Legend>
                                        <span>적음</span>
                                        {ACTIVITY_LEVELS.map(({ level, label }) => (
                                            <Tooltip key={level} message={label} position="top" width="14px">
                                                <LegendCell as="span" $level={level} aria-label={label} />
                                            </Tooltip>
                                        ))}
                                        <span>많음</span>
                                    </Legend>
                                    <SummaryDivider />
                                    <Text variant="body-sm-400" color="var(--color-Grey1)">
                                        총 {activityRange?.total_count || 0}건
                                    </Text>
                                </ActivitySummary>
                            </SectionHeader>
                            <ActivityPanel>
                                <StripScroll>
                                    <Strip>
                                        {(activityRange?.daily || []).map((item) => {
                                            const level = activityLevel(item.total);
                                            const message = `${moment(item.date).format("M월 D일")} · 전체 ${item.total}건`;
                                            return (
                                                <Tooltip key={item.date} message={message} position="top" width="100%">
                                                    <ActivityCell
                                                        type="button"
                                                        $level={level}
                                                        $selected={selectedDate === item.date}
                                                        aria-label={message}
                                                        aria-pressed={selectedDate === item.date}
                                                        onClick={() =>
                                                            setSelectedDate((current) =>
                                                                current === item.date ? "" : item.date,
                                                            )
                                                        }
                                                    />
                                                </Tooltip>
                                            );
                                        })}
                                    </Strip>
                                </StripScroll>
                                <StripLabels>
                                    <span>{moment(activityRange?.from).format("M월 D일")}</span>
                                    <span>{moment(activityRange?.to).format("M월 D일 · 오늘")}</span>
                                </StripLabels>
                                <ActivityMetrics>
                                    <ActivityMetric>
                                        <span>최근 30일 중 활동한 날</span>
                                        <strong>{activityStats.activeDays}일</strong>
                                    </ActivityMetric>
                                    <ActivityMetric>
                                        <span>연속 활동일</span>
                                        <strong>{activityStats.consecutiveDays}일</strong>
                                    </ActivityMetric>
                                </ActivityMetrics>
                            </ActivityPanel>
                        </Section>

                        <Section>
                            <SectionHeader>
                                <SectionTitleGroup>
                                    <SectionTitle>활동 기록</SectionTitle>
                                    {selectedDate && (
                                        <SelectedDateTag
                                            aria-label={`${moment(selectedDate).format("M월 D일")} 필터 해제`}
                                            onClick={() => setSelectedDate("")}
                                        >
                                            <TagText>{moment(selectedDate).format("M월 D일")}</TagText>
                                            <Icon name="x" size="8" color="var(--color-SubBlack)" aria-hidden="true" />
                                        </SelectedDateTag>
                                    )}
                                </SectionTitleGroup>
                                <TextButton
                                    type="button"
                                    onClick={() => navigate(`/${space_uuid}/management?tab=history`)}
                                >
                                    전체 활동 보기
                                </TextButton>
                            </SectionHeader>
                            {historyQuery.isLoading ? (
                                <StateBox>최근 활동을 불러오는 중입니다.</StateBox>
                            ) : historyQuery.isError ? (
                                <StateBox>최근 활동을 불러오지 못했습니다.</StateBox>
                            ) : events.length === 0 ? (
                                <StateBox>
                                    {selectedDate
                                        ? `${moment(selectedDate).format("M월 D일")}에 기록된 활동이 없습니다.`
                                        : "최근 30일 동안 기록된 활동이 없습니다."}
                                </StateBox>
                            ) : (
                                <>
                                    <EventList>
                                        {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                                            <React.Fragment key={date}>
                                                <DateHeading>{moment(date).format("YYYY년 M월 D일")}</DateHeading>
                                                {dateEvents.map((event) => {
                                                    const preview =
                                                        event.metadata.message_preview ||
                                                        event.metadata.selected_text_preview ||
                                                        (event.metadata.node_name
                                                            ? `파일: ${event.metadata.node_name}`
                                                            : "");
                                                    return (
                                                        <EventRow key={event.id}>
                                                            <EventTime>
                                                                {moment(event.occurred_at).format("HH:mm")}
                                                            </EventTime>
                                                            <Tooltip
                                                                message={RESOURCE_LABEL[event.resource_type]}
                                                                position="top"
                                                            >
                                                                <EventIcon>
                                                                    <Icon
                                                                        name={
                                                                            COUNT_CARDS.find(
                                                                                (card) =>
                                                                                    card.key === event.resource_type,
                                                                            )?.icon
                                                                        }
                                                                        size="14px"
                                                                    />
                                                                </EventIcon>
                                                            </Tooltip>
                                                            <EventBody>
                                                                <ActivitySentence
                                                                    event={event}
                                                                    navigate={navigate}
                                                                    onMemberClick={setProfileMember}
                                                                />
                                                                {preview && <Preview>{preview}</Preview>}
                                                            </EventBody>
                                                        </EventRow>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </EventList>
                                    {historyQuery.hasNextPage && (
                                        <Button
                                            {...BUTTON_STYLES.modalSecondary}
                                            width="120px"
                                            margin="20px auto 0"
                                            disabled={historyQuery.isFetchingNextPage}
                                            onClick={() => historyQuery.fetchNextPage()}
                                            buttonText={historyQuery.isFetchingNextPage ? "불러오는 중" : "더 보기"}
                                        />
                                    )}
                                </>
                            )}
                        </Section>
                        {profileMember && (
                            <SpaceMemberProfileModal
                                readOnly
                                member={profileMember}
                                onClose={() => setProfileMember(null)}
                            />
                        )}
                    </>
                )}
            </DashboardBody>
        </LoginUserLayout>
    );
}

export default DashboardPage;
